// Copyright (c) 2019 Comsearch, Inc., All rights reserved

import React from 'react'
import { error, success } from 'react-notification-system-redux'
import { reset } from 'redux-form'
import { patchBulkSiteEdit } from 'helpers/api'
import { BULK_EDITS_FORM } from 'components/BulkUpdates/constants'
import { setBulkEditPaths } from 'redux/modules/bulkEdit/actions'
import getUpdatedBulkEditPathsDataByPathId from 'redux/modules/bulkEdit/thunks/getUpdatedBulkEditPathsDataByPathId'
import compareBulkSiteChangesForCallsignCascade from '../compareBulkSiteChangesForCallsignCascade'
import { clearPathAndSiteHighlights, filterAndSortPaths } from 'redux/modules/bulkEdit'
import { handleCallsignCascadeMatches } from 'redux/helpers'
import getPathIdsFromSegmentEndIds from './getPathIdsFromSegmentEndIds'

import {
  checkPathsWarningValidations,
  removeAllPathValidationsFromDrawer,
} from 'redux/modules/pathValidationsDrawer'

import {
  formatAdministration,
  formatAntennas,
  formatLosses,
  formatRadio,
  formatRegulatory,
  formatSite,
} from 'redux/modules/pathDetails/thunks/handlePathDetailsSubmit/helpers'

// after the user has confirmed that they want to update the highlighted sites,
// this fires, taking the Bulk Edit - Site Mode form's data and sending it to the
// API along with the segmentEndIds to be updated.

// NOTE - any errors will be thrown to the parent to handle

const submitSiteMode = (formValues, d, formProps) => async (dispatch, getState) => {
  const {
    openContextModal,
    updateStoredPathsAfterBulkEdit,
  } = formProps

  // these are the segment ends that we're going to update
  const segmentEndIds = getState().bulkEdit.get('highlightedSiteIds').toJS()
  const pathIds = dispatch(getPathIdsFromSegmentEndIds(segmentEndIds))

  const callsignCascadeTriggerData = dispatch(compareBulkSiteChangesForCallsignCascade(formValues))

  const {
    administration,
    antennas,
    losses,
    radios,
    regulatory,
    sites,
  } = formValues

  const setGroundElevations = !!formValues.setGroundElevations.length

  // grab these values from the antenna in the form
  const {
    antennaSpecId,
    centerline,
    gain,
  } = formatAntennas(antennas[0])[0]

  // if all these values are null, then there's nothing we actually want to set for the antenna,
  // in that case just pass primaryAntenna: null.
  // else make an object with the updates that we want for primaryAntenna
  // since this is Bulk Edit, we're not talking about Antennas like on path details.
  const primaryAntenna = (
    antennaSpecId === null &&
    centerline === null &&
    gain === null
      ? null
      : {
        antennaSpecId,
        centerline,
        gain,
      }
  )

  const bulkUpdate = {
    ...formatSite(sites[0]),
    ...formatLosses(losses[0]),
    ...formatRadio(radios[0]),
    ...formatRegulatory(regulatory[0]),
    ...formatAdministration(administration[0]),
    primaryAntenna,
    segmentEndIds,
    setGroundElevations,
  }

  // since siteName is a required value, Path Details does not cast this to null when empty.
  if (bulkUpdate.siteName === '') {
    bulkUpdate.siteName = null
  }

  // a list of properties to delete from the object which
  // are used in Path Details but are not used on Bulk Edits.
  delete bulkUpdate.acmCoordinationAdvantageFlag
  delete bulkUpdate.atpc
  delete bulkUpdate.passiveFlag
  delete bulkUpdate.segmentEndId

  // mapping this field is kinda weird. it should be
  // called groundElevation everywhere but...
  // in Path Details, the API calls is 'elevation'.
  // This will be fixed app wide but is pretty low hanging fruit.
  bulkUpdate.groundElevation = bulkUpdate.elevation
  delete bulkUpdate.elevation

  const {
    message,
    status,
  } = await patchBulkSiteEdit(bulkUpdate)

  const updatedBulkEditPathsData = await dispatch(getUpdatedBulkEditPathsDataByPathId(pathIds))

  // update bulkEdit redux store with the path data
  dispatch(setBulkEditPaths(updatedBulkEditPathsData))
  // in case these changes have just removed segment ends / paths from filtered list of visible paths,
  // we need to trigger an update to the visible paths.
  dispatch(filterAndSortPaths())

  // tell the original store of the path data that these paths have been updated.
  // the parent store can figure out what to do about that.
  // NOTE - we don't await this so the warnings can proceed in parallel.
  updateStoredPathsAfterBulkEdit(pathIds)

  // so since we're on bulk edits, there are a bunch of different ways in which the user can
  // generate errors for multiple paths. As there is no clear requirement here,
  // the simplest solution for handling these errors is by clearing any previous errors
  await dispatch(removeAllPathValidationsFromDrawer())

  const handleSuccessfulCascadeCallback = () => updateStoredPathsAfterBulkEdit(pathIds)

  dispatch(handleCallsignCascadeMatches(callsignCascadeTriggerData, openContextModal, handleSuccessfulCascadeCallback))

  // if we've gotten here, then we'll check for warnings for these paths
  // the second argument is a callback after the warnings check.
  await dispatch(checkPathsWarningValidations(pathIds, () => {
    // clear all the highlighted sites in bulk edits
    dispatch(clearPathAndSiteHighlights())

    // reset the Bulk Edit Update form
    dispatch(reset(BULK_EDITS_FORM))

    const idRoot = 'notification-successful-bulk-edit-save'

    if (status === 'Error') {
      // This is pretty edge case and probably isn't possible with valid db data but...
      // If the Path Save was successful, but the Callsign Cascade 1 failed,
      // the API will respond with HTTP 206, providing the updated Path Details entity
      // but also providing a message to present to the user telling them of the Callsign Cascade 1 failure.
      // This message will tell the user something to the effect of:
      // Note: Call sign cascade for (expiration date / radio service / station class) did not occur - please contact customer support.
      dispatch(error({
        autoDismiss: 0,
        message,
        title: 'Error:',
      }))
    } else {
      // update UI to show user success message
      dispatch(success({
        autoDismiss: 5,
        message: (
          <span id={`${idRoot}-message`}>
            {message}
          </span>
        ),
        uid: idRoot,
      }))
    }
  }))
}

export default submitSiteMode
